<template>
  <b-alert v-if="showBanner" show class="promo-alert-banner" variant="info" :class="{ 'premium-gated': premiumGateEnabled }">
    <div class="d-flex align-items-center justify-content-between gap-3 w-100">
      <span class="promo-text d-flex align-items-center gap-3">
        <div class="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 tag-icon">
          <img src="/img/gift.svg" alt="Tag Icon" />
        </div>
        Limited time offer: {{ remainingTime }} Save up to 50% for 3 Months
      </span>
      <div class="d-flex align-items-center gap-1 gap-md-3">
        <button class="save-btn text-nowrap" @click="navigateToBilling">Save Now</button>
        <button class="d-flex align-items-center justify-content-center custom-close-btn" @click="closeBanner">
          <img src="img/x-black.svg" width="14" height="14" alt="Close Icon" />
        </button>
      </div>
    </div>
  </b-alert>
</template>

<script>
import { PLAN_SKU_TYPES, ROUTES } from "../../utils/constants";
import { getCookie, isEligibleForFirstTimeOffer, setCookie } from "../../utils/helpers";

const TWO_HOURS_IN_MILLISECONDS = 2 * 60 * 60 * 1000;
const TIME_REMAINING = {
  INITIAL: '02:00:00',
  EXPIRED: '00:00:00'
}
const { FREE_PLAN, SUSPENDED_PLAN, TRIAL_PLAN } = PLAN_SKU_TYPES;

export default {
  props: ['premiumGateEnabled'],
  name: "PromoBanner",
  data() {
    return {
      showBanner: false,
      startTime: null,
      promoEndTime: null,
      timerInterval: null,
      remainingTime: TIME_REMAINING.INITIAL
    };
  },
  computed: {
    isFreeUser() {
      const currentUserPlan = _.get(this, '$A.Auth.account.subscription.planSku', FREE_PLAN);
      return currentUserPlan === FREE_PLAN || currentUserPlan === SUSPENDED_PLAN || currentUserPlan === TRIAL_PLAN;
    },
  },
  methods: {
    closeBanner() {
      this.showBanner = false;
      setCookie('isPromoBannerClosed', 'true');
      this.clearPromoTimer();
    },
    navigateToBilling() {
      if (this.$route.path === ROUTES.BILLING) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.$router.push(ROUTES.BILLING);
      }
    },
    isPromoBannerClosed() {
      return getCookie('isPromoBannerClosed') === 'true';
    },
    isUserOnStepOne() {
      return this.$A.Auth?.onboardingStep === 1 || this.$A.Auth?.onboardingStep === null;
    },
    updateRemainingTime() {
      if (!this.promoEndTime) {
        this.remainingTime = TIME_REMAINING.INITIAL;
        return;
      }

      const now = new Date();
      const endTime = new Date(this.promoEndTime);
      
      if (isNaN(endTime.getTime())) {
        console.error('Invalid promo end time:', this.promoEndTime);
        this.remainingTime = TIME_REMAINING.INITIAL;
        return;
      }

      const remainingTime = endTime - now;
      
      if (remainingTime <= 0) {
        this.closeBanner();
        this.remainingTime = TIME_REMAINING.EXPIRED;
        return;
      }

      const totalSeconds = Math.floor(remainingTime / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.remainingTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    startTimer() {
      if (this.isUserOnStepOne() || this.isPromoBannerClosed()) {
        console.log('Banner not shown - early return conditions met');
        this.showBanner = false;
        return;
      }

      if (!isEligibleForFirstTimeOffer(this.$A.Auth, this.isFreeUser)) {
        this.showBanner = false;
        this.clearPromoTimer();
        return;
      }

      const onboardedAtRaw = _.get(this, '$A.Auth.onboardedAt', null);
      if (!onboardedAtRaw) {
        this.showBanner = false;
        this.clearPromoTimer();
        return;
      }

      try {
        const onboardedAt = new Date(onboardedAtRaw + 'Z');
        if (isNaN(onboardedAt.getTime())) throw new Error('Invalid onboardedAt');

        const promoEndTime = new Date(onboardedAt.getTime() + TWO_HOURS_IN_MILLISECONDS);
        const now = new Date();
        
        if (now >= promoEndTime) {
          this.showBanner = false;
          this.clearPromoTimer();
          return;
        }
        this.startTime = onboardedAt;
        this.promoEndTime = promoEndTime;
        this.showBanner = true;
        this.updateRemainingTime();
      } catch (error) {
        this.showBanner = false;
        this.clearPromoTimer();
      }
    },
    clearPromoTimer() {
      this.startTime = null;
      this.promoEndTime = null;
    }
  },
  watch: {
    showBanner: function() {
      this.$emit("banner-visbility", this.showBanner);
    },
    isFreeUser: {
      handler: function(newValue) {
        this.startTimer();
      },
      immediate: false
    }
  },
  mounted() {
    this.startTimer();
    this.timerInterval = setInterval(() => {
      if (this.showBanner && this.promoEndTime) {
        if (new Date() >= this.promoEndTime) {
          this.closeBanner();
        } else {
          this.updateRemainingTime();
        }
      }
    }, 1000);
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }
};
</script>

<style>
.promo-alert-banner {
  background-color: #e8f5e9;
  padding: 10px 16px;
  display: flex;
  justify-content: space-between;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  height: 50px;
  align-items: center;

  &.premium-gated {
    z-index: 20002;
  }
}

.promo-alert-banner .promo-text {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #1B1B1C;
}

.promo-alert-banner .save-btn {
  background-color: white;
  color: #388523;
  border: none;
  padding: 5px 13px;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  text-align: center;
}

.promo-alert-banner .close {
  color: black !important;
  text-shadow: none !important;
  opacity: 1 !important;
}

.promo-alert-banner .tag-icon {
  width: 24px;
  height: 24px;
  background-color: #388523;
}

@media (max-width: 576px) {
  .promo-text{
    font-size: 12px !important;
  }
  .save-btn{
    font-size: 12px !important;
    padding: 4px !important;
    line-height: normal !important;
  }
  .promo-alert-banner {
    padding: 5px 8px;
  }
}

.custom-close-btn {
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #1B1B1C;
}

.custom-close-btn:hover {
  opacity: 0.7;
}

</style>
